import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Divider, Stack } from "@mui/material";

import { Button } from "components/UI";
import { useFinanceOnboardingStatus } from "features/finances";
import { useEffect, useState } from "react";
import { useCentralStore } from "stores";
import {
  CreateApproverModal,
  CreateBankAccountModal,
  CreateBudgetYearModal,
  CreateChartOfAccountModal,
  DefaultChartOfAccountsModal
} from "../Modals";
import { Steppers } from "../Steppers";
import { BankAccounts } from "./BankAccounts";
import { BudgetYear } from "./BudgetYear";
import { ChartOfAccounts } from "./ChartOfAccounts";
import { ChooseApprover } from "./ChooseApprover";
import { LegalName } from "./LegalName";
import { Success } from "./Success";
import { Welcome } from "./Welcome";

export const Onboarding = ({ steps }) => {
  const setHeaderTitle = useCentralStore(state => state.setHeaderTitle);
  const [activeStep, setActiveStep] = useState(0);

  const { isOnboardingComplete } = useFinanceOnboardingStatus();

  useEffect(() => {
    setHeaderTitle("Finances");

    return () => setHeaderTitle("");
  }, []);

  const allViews = {
    0: { component: <Welcome />, always: true },
    1: { component: <LegalName />, always: true },
    2: { component: <BudgetYear />, show: steps?.budgetYears },
    3: { component: <ChartOfAccounts />, show: steps?.chartOfAccounts },
    4: { component: <BankAccounts />, show: steps?.banks },
    5: { component: <ChooseApprover />, show: steps?.approvers },
    6: {
      component: <Success />,
      always: isOnboardingComplete
    }
  };

  const stepLabels = {
    0: "Welcome",
    1: "Legal Name",
    2: "Budget Year",
    3: "Chart of Accounts",
    4: "Bank Accounts",
    5: "Approvers",
    6: "Complete"
  };

  const filteredViews = Object.entries(allViews)
    .filter(([, view]) => view.always || view.show)
    .map(([key, view]) => ({ index: parseInt(key), view }));

  const activeViews = filteredViews.reduce((acc, { view }, index) => {
    acc[index] = view.component;
    return acc;
  }, {});

  const activeStepLabels = filteredViews.map(({ index }) => stepLabels[index]);

  const handleStepClick = filteredIndex => {
    setActiveStep(filteredIndex);
  };

  const handleNext = () => {
    if (activeStep < Object.keys(activeViews).length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  return (
    <Stack direction="column" spacing={6} minWidth="900px">
      <Steppers
        activeStep={activeStep}
        onClick={handleStepClick}
        steps={activeStepLabels}
      />
      {activeViews[activeStep]}
      <Divider />
      <Stack direction="row" alignSelf="flex-end" spacing={2}>
        {activeStep !== 0 && (
          <Button
            flavor="outlined"
            startIcon={<ChevronLeft />}
            onClick={handleBack}
          >
            Back
          </Button>
        )}
        {activeStep < Object.keys(activeViews).length - 1 && (
          <Button endIcon={<ChevronRight />} onClick={handleNext}>
            Next
          </Button>
        )}
      </Stack>

      {steps?.budgetYears && <CreateBudgetYearModal />}
      {steps?.chartOfAccounts && <DefaultChartOfAccountsModal />}
      {steps?.banks && <CreateBankAccountModal />}
      {steps?.banks && <CreateChartOfAccountModal />}
      {steps?.approvers && <CreateApproverModal />}
    </Stack>
  );
};
